import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSafeNavigate } from '../hooks/useUnsavedChangesWarning';
import { useTheme, useMediaQuery } from '@mui/material';
import { keyframes } from '@emotion/react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Person,
  Business,
  Logout,
} from '@mui/icons-material';
import { authUtils } from '../services/api';
import { useTranslation } from 'react-i18next';

// Animation de clignotement pour le chip DEV (période de 5 secondes)
const blinkAnimation = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
`;

const Navigation = ({ onMenuToggle, onDesktopMenuToggle, desktopMenuOpen }) => {
  const navigate = useSafeNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentUser, setCurrentUser] = useState(authUtils.getCurrentUser());
  const isAuthenticated = authUtils.isAuthenticated();
  
  // État pour masquer le chip DEV (réinitialisé à chaque chargement)
  const [devChipHidden, setDevChipHidden] = useState(false);

  // Écouter les mises à jour du profil utilisateur
  useEffect(() => {
    const handleUserProfileUpdate = (event) => {
      setCurrentUser(event.detail.user);
    };

    window.addEventListener('userProfileUpdated', handleUserProfileUpdate);
    
    return () => {
      window.removeEventListener('userProfileUpdated', handleUserProfileUpdate);
    };
  }, []);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Fonctions mobile menu supprimées - gérées maintenant par Layout.js

  const handleLogout = async () => {
    try {
      await authUtils.logout();
      // La redirection est gérée par authUtils.logout()
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      // Forcer la déconnexion même en cas d'erreur
      authUtils.clearAuthInfo();
      navigate('/login');
    }
    handleMenuClose();
  };

  // Navigation items supprimés - utilisés maintenant dans Layout.js

  const adminItems = [
    { label: t('navigation.organizationManagement'), path: '/tenant', icon: <Business /> },
  ];

  const userMenuItems = [
    { label: t('navigation.myProfile'), path: '/profile', icon: <Person /> },
    ...(currentUser?.role === 'admin' ? adminItems : []),
    { label: t('navigation.logout'), action: handleLogout, icon: <Logout /> },
  ];

  if (!isAuthenticated) {
    return null;
  }

  // Déterminer la couleur selon l'environnement
  const isDev = process.env.NODE_ENV === 'development' || 
                window.location.hostname === 'localhost' || 
                window.location.hostname === '192.168.1.52';

  // Fonction pour masquer le chip DEV
  const handleDevChipClick = (e) => {
    e.stopPropagation(); // Empêcher la navigation vers la page d'accueil
    setDevChipHidden(true);
  };

  // Obtenir l'initiale de l'utilisateur
  const getUserInitial = () => {
    if (currentUser?.prenom) {
      return currentUser.prenom.charAt(0).toUpperCase();
    }
    if (currentUser?.nom) {
      return currentUser.nom.charAt(0).toUpperCase();
    }
    if (currentUser?.email) {
      return currentUser.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Obtenir le nom d'utilisateur (partie avant @ de l'email ou prénom+nom en minuscules)
  const getUsername = () => {
    if (currentUser?.email) {
      return currentUser.email.split('@')[0];
    }
    if (currentUser?.prenom && currentUser?.nom) {
      return `${currentUser.prenom}${currentUser.nom}`.toLowerCase().replace(/\s+/g, '');
    }
    return t('navigation.user');
  };


  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        backgroundColor: 'white', 
        color: 'text.primary',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        zIndex: (theme) => theme.zIndex.drawer + 1 
      }}
    >
      <Toolbar>
        {/* Menu mobile et desktop */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={isMobile ? onMenuToggle : onDesktopMenuToggle}
          sx={{ mr: 2, color: 'text.primary' }}
        >
          <MenuIcon />
        </IconButton>

        {/* Logo/Titre */}
        <Box
          sx={{ 
            flexGrow: 1, 
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center'
          }}
          onClick={() => navigate('/')}
        >
          <Box
            component="img"
            src="/libranize-logo.svg"
            alt="Libranize"
            sx={{
              height: '40px',
              width: 'auto',
              maxWidth: '4000px',
            }}
          />
          {isDev && !devChipHidden && (
            <Chip 
              label="DEV" 
              size="small" 
              onClick={handleDevChipClick}
              sx={{ 
                ml: 2, 
                bgcolor: 'error.main',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.7rem',
                cursor: 'pointer',
                animation: `${blinkAnimation} 3s infinite`,
                '&:hover': {
                  opacity: 0.8,
                  animation: 'none'
                }
              }} 
            />
          )}
        </Box>

        {/* Navigation desktop supprimée - gérée maintenant par Layout.js */}

        {/* Menu utilisateur */}
        <Button
          onClick={handleMenuOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            backgroundColor: 'white',
            color: 'text.primary',
            textTransform: 'none',
            border: '1px solid',
            borderColor: 'grey.300',
            '&:hover': {
              backgroundColor: 'grey.50',
            }
          }}
        >
          <Avatar 
            sx={{ 
              width: 32, 
              height: 32, 
              bgcolor: 'primary.main',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: 600
            }}
          >
            {getUserInitial()}
          </Avatar>
          <Typography 
            variant="body2" 
            sx={{ 
              display: { xs: 'none', sm: 'block' },
              fontWeight: 500
            }}
          >
            {getUsername()}
          </Typography>
        </Button>

        {/* Menu utilisateur dropdown */}
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
            }
          }}
        >
          {/* Informations utilisateur */}
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2" color="text.primary">
              {currentUser?.prenom} {currentUser?.nom}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentUser?.email}
            </Typography>
          </Box>
          <Divider />
          
          {/* Actions utilisateur */}
          {userMenuItems.map((item, index) => (
            <MenuItem
              key={index}
              onClick={() => {
                if (item.action) {
                  item.action();
                } else if (item.path) {
                  navigate(item.path);
                  handleMenuClose();
                }
              }}
              sx={{
                color: item.path && location.pathname === item.path ? 'primary.main' : 'inherit',
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>

        {/* Menu mobile supprimé - géré maintenant par Layout.js */}
      </Toolbar>
    </AppBar>
  );
};

export default Navigation;
